import { Fragment, h, render } from "preact";
import { useEffect, useState, useRef } from "preact/hooks";
import { Thumbs, EffectFade, Pagination, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import {
  FaqIcon,
  WarrantyIcon,
  ArrowLeft,
  ArrowRight,
} from "../helpers/icons.jsx";
import ZoomableImage from "./zoomable-image.jsx";
import ReactModal from "../helpers/react-modal.jsx";

import storefront from "../helpers/storefront";
import * as queries from "../helpers/storefront/queries";
import FadeImage from "./fade-image.jsx";

const ZoomMessage = () => {
  const [show, setShow] = useState(true);

  useEffect(() => {
    setTimeout(() => {
      setShow(false);
    }, 2000);
  }, []);

  return (
    <div
      style={{ opacity: show ? 1 : 0 }}
      className="h5 product-media__zoom-message"
    >
      Click to Zoom
    </div>
  );
};

const areDifferentColor = (variant1, variant2) => {
  try {
    const findColor = (options) =>
      options.find((option) => option.name === "Color");
    const color1 = findColor(variant1.selectedOptions);
    const color2 = findColor(variant2.selectedOptions);
    return color1.value !== color2.value;
  } catch (e) {
    return false;
  }
};

const MediaContent = ({ data }) => {
  const [swiper, setSwiper] = useState(null);
  const [modalSwiper, setModalSwiper] = useState(null);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [product, setProduct] = useState(data.product);
  const [currentVariant, setCurrentVariant] = useState(product.variants[0]);
  const [productHandle, setProductHandle] = useState(null);
  const [oos, setOos] = useState(null);
  const [media, setMedia] = useState(product.media);
  const [galleryFade, setGalleryFade] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [initialModalSlide, setInitialModalSlide] = useState(0);
  const [firstImageOpacity, setFirstImageOpacity] = useState(0);
  let screenMobile = window.matchMedia("(max-width: 992px)");

  const handleMediaClick = (slideIndex) => {
    setInitialModalSlide(slideIndex);
    setModalOpen(true);
  };

  const variantImages = product.variants.map(
    (edge) => edge.featured_image?.src,
  );
  const [variantImage, setVariantImage] = useState(
    product?.variants[0]?.featured_image?.src,
  );

  const images = media.filter((i) => {
    return !variantImages.find((v) => {
      return v === i.src;
    });
  });

  const [filteredMedia, setFilteredMedia] = useState(images);

  const swiperModalParams = {
    slidesPerView: "auto",
    spaceBetween: 0,
    direction: "horizontal",
    effect: "fade",
    noSwipingClass: "swiper-slide",
    initialSlide: initialModalSlide,
  };
  const swiperModalThumbParams = {
    direction: "horizontal",
    spaceBetween: 20,
    slidesPerView: "auto",
    watchSlidesProgress: true,
    centerInsufficientSlides: true,
    slidesPerGroup: 1,
    initialSlide: initialModalSlide,
    breakpoints: {
      768: {
        direction: "vertical",
      },
    },
    grid: {
      rows: 1,
    },
  };
  const swiperParams = {
    modules: [Pagination, Thumbs, Navigation],
    thumbs: {
      swiper: thumbsSwiper,
    },
    pagination: {
      clickable: true,
      dynamicBullets: true,
      dynamicMainBullets: 3,
      enabled: true,
    },
    navigation: {
      nextEl: "[data-product-gallery-navigation-next]",
      prevEl: "[data-product-gallery-navigation-prev]",
    },
    slidesPerView: 1,
    speed: 300,
    effect: "fade",
    fadeEffect: { crossFade: true },
    loop: true,
    rebuildOnUpdate: true,
    breakpoints: {
      992: {
        pagination: {
          enabled: false,
        },
      },
    },
  };

  const thumbsSwiperParams = {
    modules: [Thumbs],
    spaceBetween: 16,
    slidesPerView: "auto",
    watchSlidesProgress: true,
    centerInsufficientSlides: true,
    slidesPerGroup: 1,
  };

  useEffect(() => {
    const handleProductUpdate = (e) => {
      let v = e?.detail?.variant,
        p = e.detail.product;
      setCurrentVariant(v);
      v?.featured_image?.src && setVariantImage(v.featured_image.src);
      v?.image?.src && setVariantImage(v.image.src);
      p?.handle && setProductHandle(p?.handle);
      window.setVariantImage = setVariantImage;
      if (
        window.matchMedia("(max-width: 992px)").matches &&
        areDifferentColor(currentVariant, v)
      ) {
        document
          .querySelector("[data-product-media]")
          .scrollIntoView({ behavior: "smooth", block: "center" });
      }

      thumbsSwiper?.slideTo(0, 300);
      swiper?.slideTo(0, 300);
    };
    document.addEventListener("productUpdate", handleProductUpdate, false);

    return () => {
      document.removeEventListener("productUpdate", handleProductUpdate, false);
    };
  }, [
    swiper,
    setVariantImage,
    setProductHandle,
    setCurrentVariant,
    currentVariant,
  ]);

  useEffect(async () => {
    if (!product) return;
    const response = await storefront.request({
      query: queries.product,
      variables: {
        handle: product.handle,
      },
    });

    let p = response.data?.productByHandle,
      variants = p?.variants?.edges,
      variant = variants ? variants[0]?.node : null;
    if (!variant) return;
    setCurrentVariant(variant);
  }, [product]);

  const getAltText = (item) => {
    let fullBleed =
      item?.alt?.includes("#full-bleed") ||
      item?.altText?.includes("#full-bleed");
    return fullBleed ? product?.title : item.alt;
  };

  const getImagePadding = (item) => {
    let fullBleed =
      item?.alt?.includes("#full-bleed") ||
      item?.altText?.includes("#full-bleed");
    return fullBleed ? "full" : "";
  };

  const getImageFit = (item) => {
    let fullBleed =
      item?.alt?.includes("#full-bleed") ||
      item?.altText?.includes("#full-bleed");
    return fullBleed ? "cover" : "contain";
  };

  useEffect(async () => {
    if (!productHandle) return;
    setGalleryFade(true);
    const response = await storefront.request({
      query: queries.product,
      variables: {
        handle: productHandle,
      },
    });

    if (!response?.data?.productByHandle) return;
    let sp = response.data.productByHandle,
      si = sp?.media.edges,
      v = sp?.variants.edges,
      vim = v && v[0]?.image?.src;

    let updatedImages = si.filter((i) => {
      if (i.node.mediaContentType != "IMAGE") return true;
      return !v.find((vi) => {
        return (
          vi.node.src === i.node.image?.src ||
          vi?.node?.image?.src === i.node.image?.src
        );
      });
    });
    updatedImages = updatedImages.map((i) => i.node);
    updatedImages = updatedImages.map((i) => (i.image ? i.image : i));
    setFilteredMedia(updatedImages);
    setTimeout(() => {
      setGalleryFade(false);
    }, 200);
  }, [productHandle]);

  const handleThumbClick = (index) => modalSwiper && modalSwiper.slideTo(index);

  useEffect(() => {
    let available = currentVariant.availableForSale || currentVariant.available,
      quantity =
        (currentVariant.inventoryQuantity &&
          currentVariant.inventoryQuantity > 0) ||
        (currentVariant.quantityAvailable &&
          currentVariant.quantityAvailable > 0);

    let message = currentVariant?.oosMessage?.value
      ? currentVariant?.oosMessage?.value
      : product.oosMessage;

    !available || (!quantity && setOos(message));
  }, [currentVariant]);

  return (
    <Fragment>
      {data.warranty && (
        <div className="product-media__warranty copy-text">
          <WarrantyIcon />
          {data.warranty}
        </div>
      )}
      {data.faqs && (
        <a className="product-media__faqs-badge caption" href="#productFaqs">
          <FaqIcon />
          FAQ
        </a>
      )}
      <Swiper
        {...swiperParams}
        onSwiper={setSwiper}
        className="product-media__container"
        data-product-media-gallery
      >
        <div slot="container-end">
          <div className="product-media__navigation-wrapper">
            <button
              className="product-media__navigation"
              data-product-gallery-navigation-prev
            >
              <span className="visually-hidden">Previous image</span>
              <ArrowLeft />
            </button>
            <button
              className="product-media__navigation"
              data-product-gallery-navigation-next
            >
              <span className="visually-hidden">Next image</span>
              <ArrowRight />
            </button>
          </div>
        </div>
        {currentVariant && variantImage && (
          <SwiperSlide
            data-product-image
            data-slide={0}
            onClick={() => handleMediaClick(0)}
          >
            <FadeImage
              className={`product-media__image product-media__image--${getImageFit(
                currentVariant.featured_image || currentVariant.image,
              )} ${galleryFade ? "fade" : ""}`}
              src={getSizedImageUrl(variantImage, "1200x")}
              alt={getAltText(
                currentVariant.featured_image || currentVariant.image,
              )}
            />
          </SwiperSlide>
        )}
        {filteredMedia.map((item, index) => (
          <SwiperSlide
            data-product-image
            data-slide={variantImage ? index + 1 : index}
            onClick={() => handleMediaClick(variantImage ? index + 1 : index)}
            key={item?.src}
          >
            {item.mediaContentType?.toLowerCase() === "image" ? (
              <img
                src={getSizedImageUrl(item?.src, "1200x")}
                height={600 * (item ? item?.aspect_ratio : 1)}
                width={600}
                alt={getAltText(item)}
                className={`product-media__image product-media__image--${getImageFit(item)} ${
                  galleryFade ? "fade" : ""
                }`}
                loading={index === 0 && !variantImage ? false : "false"}
                onLoad={() => index === 0 && setFirstImageOpacity(1)}
                style={{ opacity: firstImageOpacity }}
              />
            ) : item.mediaContentType?.toLowerCase() === "external_video" ||
              item.media_type?.toLowerCase() === "external_video" ? (
              <div className="video video--product">
                <iframe
                  frameborder="0"
                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                  allowfullscreen="allowfullscreen"
                  src={`https://player.vimeo.com/video/${
                    item.external_id
                      ? item.external_id
                      : item.embeddedUrl.split("/").pop()
                  }?background=1&controls=0`}
                  title={getAltText(item)}
                ></iframe>
              </div>
            ) : (
              <img
                src={getSizedImageUrl(item?.src, "1200x")}
                height={600 * (item ? item?.aspect_ratio : 1)}
                width={600}
                alt={getAltText(item)}
                className={`product-media__image product-media__image--${getImageFit(item)} ${
                  galleryFade ? "fade" : ""
                }`}
                loading={index === 0 && !variantImage ? false : "false"}
                onLoad={() => index === 0 && setFirstImageOpacity(1)}
                style={{ opacity: firstImageOpacity }}
              />
            )}
          </SwiperSlide>
        ))}
      </Swiper>
      <Swiper
        {...thumbsSwiperParams}
        onSwiper={setThumbsSwiper}
        className="product-media__thumbs"
      >
        {currentVariant && variantImage && (
          <SwiperSlide>
            <button type="button" className="product-media__thumb-button">
              <img
                src={getSizedImageUrl(variantImage, "80x")}
                height="80"
                width="80"
                loading="lazy"
                className={`image product-media__thumb-image`}
                alt={getAltText(
                  currentVariant.featured_image || currentVariant.image,
                )}
              />
            </button>
          </SwiperSlide>
        )}
        {filteredMedia.map((item, index) => (
          <SwiperSlide
            onClick={() => handleThumbClick(variantImage ? index + 1 : index)}
          >
            <button type="button" className="product-media__thumb-button">
              <img
                src={
                  item?.preview_image
                    ? getSizedImageUrl(item?.preview_image?.src, "100x")
                    : item?.src
                      ? getSizedImageUrl(item?.src, "100x")
                      : getSizedImageUrl(
                          item?.previewImage?.originalSrc,
                          "100x",
                        )
                }
                alt={getAltText(item)}
                className={`image product-media__thumb-image`}
                height="80"
                width="80"
                loading="lazy"
              />
            </button>
          </SwiperSlide>
        ))}
      </Swiper>
      <ReactModal open={modalOpen} setOpen={setModalOpen}>
        <ZoomMessage />
        <Swiper
          {...swiperModalParams}
          onSwiper={setModalSwiper}
          modules={[EffectFade]}
          className="product-media__modal"
        >
          {variantImage && (
            <SwiperSlide data-product-image data-slide={0}>
              <ZoomableImage src={getSizedImageUrl(variantImage, "1200x")} />
            </SwiperSlide>
          )}
          {filteredMedia.map((item, index) => (
            <SwiperSlide data-product-image data-slide={index}>
              <ZoomableImage
                src={
                  item?.preview_image
                    ? getSizedImageUrl(item?.preview_image?.src, "1200x")
                    : getSizedImageUrl(item?.src, "1200x")
                }
                alt={getAltText(item)}
              />
            </SwiperSlide>
          ))}
        </Swiper>
        <Swiper
          {...swiperModalThumbParams}
          modules={[EffectFade]}
          watchSlidesProgress
          className="product-media__modal-thumbs"
        >
          {currentVariant && variantImage && (
            <SwiperSlide onClick={() => handleThumbClick(0)}>
              <img
                src={getSizedImageUrl(variantImage, "80x")}
                height="80"
                width="80"
                loading="lazy"
                className={`image image--product ${getImagePadding(
                  currentVariant.featured_image || currentVariant.image,
                )} ${galleryFade ? "fade" : ""}`}
                alt={getAltText(
                  currentVariant.featured_image || currentVariant.image,
                )}
              />
            </SwiperSlide>
          )}
          {filteredMedia.map((item, index) => (
            <SwiperSlide
              onClick={() => handleThumbClick(variantImage ? index + 1 : index)}
            >
              <img
                src={
                  item?.preview_image
                    ? getSizedImageUrl(item?.preview_image?.src, "100x")
                    : getSizedImageUrl(item?.src, "100x")
                }
                alt={getAltText(item)}
                className={`image image--product ${getImagePadding(item)}`}
                height="80"
                width="80"
                loading="lazy"
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </ReactModal>
    </Fragment>
  );
};

export default MediaContent;
